/*
  Sahara Agate — automatic free-gift engine (with diagnostics).
  Keeps the right free gifts in the cart based on amount/quantity goal tiers.
  Reports WHY a gift was or wasn't added: in the cart UI ([data-cart-gifts-status]) and
  in the console (window.cartGiftsDebug() for a full report).
  For a gift to be ₹0 at checkout the gift product should be priced ₹0 (or covered by an
  automatic discount) — the theme only auto-adds / locks / labels it.
*/
(function () {
  const CFG_SEL = '[data-cart-gifts-config]';
  const GIFT_PROP = '_gift';
  let busy = false;
  let debounceTimer = null;
  let lastReport = null;

  const log = function () { try { console.log.apply(console, ['[cart-gifts]'].concat([].slice.call(arguments))); } catch (e) {} };
  const warn = function () { try { console.warn.apply(console, ['[cart-gifts]'].concat([].slice.call(arguments))); } catch (e) {} };

  function root () {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url) || '/';
  }
  function cartUrl (path) {
    let r = root();
    if (r === '/' || !r) return path;            // avoid '//cart.js' (a cross-origin URL)
    if (r.endsWith('/')) r = r.slice(0, -1);     // e.g. localized '/en/' -> '/en'
    return r + path;
  }

  function readConfig () {
    const el = document.querySelector(CFG_SEL);
    if (!el) return { error: 'no-config', detail: 'Cart-goal config not found in the page. The cart goal may be hidden (empty cart) or no goal is configured.' };
    try { return JSON.parse(el.textContent); } catch (e) { return { error: 'bad-config', detail: 'Could not parse the cart-goal config JSON.' }; }
  }

  function qualifying (cart) {
    let amount = 0, qty = 0;
    (cart.items || []).forEach(function (it) {
      const props = it.properties || {};
      if (props[GIFT_PROP]) return;
      amount += it.final_line_price;
      qty += it.quantity;
    });
    return { amount: amount, qty: qty };
  }

  function metTiers (cfg, q) {
    return cfg.tiers.map(function (t) {
      const cur = t.type === 'quantity' ? q.qty : q.amount;
      return t.threshold > 0 && cur >= t.threshold;
    });
  }

  function variantObjs (t) { return (t.variants || []); }

  function desiredIds (cfg, met) {
    const set = new Set();
    if (cfg.mode === 'latest') {
      let hi = -1;
      met.forEach(function (m, i) { if (m) hi = i; });
      if (hi >= 0) variantObjs(cfg.tiers[hi]).forEach(function (v) { set.add(v.id); });
    } else {
      cfg.tiers.forEach(function (t, i) { if (met[i]) variantObjs(t).forEach(function (v) { set.add(v.id); }); });
    }
    return set;
  }

  function infoMap (cfg) {
    const m = new Map();
    cfg.tiers.forEach(function (t) {
      variantObjs(t).forEach(function (v) {
        if (!m.has(v.id)) m.set(v.id, { title: v.title, available: v.available, productAvailable: v.product_available, tier: t.tier });
      });
    });
    return m;
  }

  function currentGiftLines (cart) {
    const map = new Map();
    (cart.items || []).forEach(function (it) {
      const props = it.properties || {};
      if (props[GIFT_PROP]) map.set(it.id, { key: it.key, quantity: it.quantity });
    });
    return map;
  }

  function post (path, body) {
    return fetch(cartUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; }); });
  }
  function getCart () {
    return fetch(cartUrl('/cart.js'), { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }
  function getSections () {
    return fetch(window.location.pathname + '?sections=offcanvas-cart,cart-count-badge').then(function (r) { return r.json(); });
  }
  function setLoading (on) {
    const c = document.querySelector('cart-container');
    if (c) c.classList.toggle('cart-gifts-loading', !!on);
  }

  function showStatus (messages, kind) {
    const el = document.querySelector('[data-cart-gifts-status]');
    if (!el) return;
    if (!messages || !messages.length) { el.hidden = true; el.innerHTML = ''; el.className = 'cart-gifts-status'; return; }
    el.hidden = false;
    el.className = 'cart-gifts-status cart-gifts-status--' + (kind || 'error');
    el.innerHTML = messages.map(function (m) { return '<span>' + m + '</span>'; }).join('');
  }

  function render (sections) {
    if (window.updateAllCartElements) window.updateAllCartElements(sections);
    else {
      const c = document.querySelector('cart-container');
      if (c && c.reloadCartElements) c.reloadCartElements(sections);
    }
    window.dispatchEvent(new CustomEvent('kt.cart.reloaded'));
  }

  async function reconcile () {
    if (busy) return;
    busy = true;
    const report = { ts: new Date().toISOString() };
    const errors = [];
    try {
      const cfg = readConfig();
      report.config = cfg;
      if (cfg && cfg.error) { warn(cfg.detail); report.note = cfg.detail; busy = false; lastReport = report; return; }
      if (!cfg || !cfg.tiers || !cfg.tiers.length) { busy = false; lastReport = report; return; }

      const anyGift = cfg.tiers.some(function (t) { return (t.variants || []).length; });
      report.anyGiftConfigured = anyGift;
      if (!anyGift) { report.note = 'No gift products are assigned to any goal.'; busy = false; lastReport = report; return; }

      const cart = await getCart();
      const q = qualifying(cart);
      const met = metTiers(cfg, q);
      const want = desiredIds(cfg, met);
      const have = currentGiftLines(cart);
      const info = infoMap(cfg);
      report.qualifying = q;
      report.met = met;
      report.want = [].concat.apply([], Array.from(want));
      report.haveGiftVariantIds = Array.from(have.keys());

      // Diagnose: goals with gifts but not reached
      cfg.tiers.forEach(function (t, i) {
        if ((t.variants || []).length && !met[i]) {
          const cur = t.type === 'quantity' ? q.qty : q.amount;
          const need = t.threshold - cur;
          report['tier' + t.tier] = 'not reached (' + (t.type === 'quantity' ? (need + ' more item(s)') : ('₹' + (need / 100).toFixed(0) + ' more')) + ')';
        }
      });

      const toRemove = [];
      const toFixQty = [];
      have.forEach(function (line, vid) {
        if (!want.has(vid)) toRemove.push(line.key);
        else if (line.quantity !== 1) toFixQty.push(line.key);
      });
      const toAdd = [];
      want.forEach(function (vid) { if (!have.has(vid)) toAdd.push(vid); });
      report.toAdd = toAdd; report.toRemove = toRemove; report.toFixQty = toFixQty;

      if (!toRemove.length && !toFixQty.length && !toAdd.length) {
        // Nothing to change — still surface a reason if a configured gift isn't present because its goal isn't met
        showStatus(null);
        busy = false; lastReport = report; log('No changes needed.', report); return;
      }

      setLoading(true);
      for (const key of toRemove) { await post('/cart/change.js', { id: key, quantity: 0 }); }
      for (const key of toFixQty) { await post('/cart/change.js', { id: key, quantity: 1 }); }

      const added = [];
      for (const vid of toAdd) {
        const gi = info.get(vid) || {};
        const props = {}; props[GIFT_PROP] = 'tier-' + (gi.tier || 1);
        const res = await post('/cart/add.js', { items: [{ id: vid, quantity: 1, properties: props }] });
        if (res.ok) {
          added.push(gi.title || vid);
        } else {
          const reason = (res.data && (res.data.description || res.data.message)) || ('HTTP ' + res.status);
          let hint = reason;
          if (gi.available === false || gi.productAvailable === false) hint = reason + ' — this gift product appears to be out of stock / unavailable.';
          const msg = 'Couldn’t add free gift “' + (gi.title || ('#' + vid)) + '”: ' + hint;
          errors.push(msg);
          warn(msg, res);
        }
      }
      report.added = added; report.errors = errors;

      const sections = await getSections();
      render(sections);
      showStatus(errors.length ? errors : null, 'error');
    } catch (e) {
      warn('Unexpected error', e);
      errors.push('Free-gift update failed: ' + (e && e.message ? e.message : e));
      showStatus(errors, 'error');
      report.exception = String(e);
    } finally {
      setLoading(false);
      busy = false;
      lastReport = report;
    }
  }

  function schedule () { clearTimeout(debounceTimer); debounceTimer = setTimeout(reconcile, 180); }

  ['kt.cart.reloaded', 'kt.cart.added', 'cart:updated'].forEach(function (ev) {
    window.addEventListener(ev, schedule);
    document.addEventListener(ev, schedule);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();

  // Manual debug helper
  window.cartGiftsDebug = async function () {
    const cfg = readConfig();
    const cart = await getCart();
    const q = cfg && cfg.tiers ? qualifying(cart) : null;
    const met = (cfg && cfg.tiers) ? metTiers(cfg, q) : null;
    const out = { config: cfg, qualifying: q, met: met, lastReport: lastReport, cartItems: (cart.items || []).map(function (i) { return { id: i.id, title: i.title, qty: i.quantity, price: i.final_line_price, props: i.properties }; }) };
    log('DEBUG REPORT', out);
    return out;
  };
})();
